import { Component, inject, OnInit } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { BaseComponent } from 'src/app/components/_base/base.component';
import { NotificationStatus, Notification } from 'src/app/types/notification';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from 'src/app/services/auth/auth.service';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { captureException } from '@sentry/angular';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { ChartImportService } from 'src/app/services/chart-import/chart-import.service';
import { chartImportJobLink } from 'src/app/shared/chart-import-job-link';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

/**
 * The backend (GOO-241) sends these two notifications with hardcoded, un-translated titles — it
 * has no per-locale copy and no structured link back to the job. Matching on the exact English
 * title is the only way this component can tell "this notification is about a chart import" apart
 * from any other notification in the same generic list.
 *
 * Known gap (backend-rooted, not fixable from this repo — same pattern as GOO-246's documented
 * gaps): once a job leaves the active-job window (`GET /chart-import-jobs/active` only returns
 * `queued`/`running`/`awaiting_review`, never `failed`), a "Size chart import failed" notification
 * clicked after a page reload has nothing to route to — `activeJob()` will already be null by then.
 * Within the same session (no reload since the job failed) it still routes correctly, since nothing
 * clears `activeJob` on failure. A real fix needs the backend to carry a job reference on the
 * notification, or to keep recently-failed jobs in the active window.
 */
const CHART_IMPORT_READY_TITLE = 'Your size chart is ready to review';
const CHART_IMPORT_FAILED_TITLE = 'Size chart import failed';

@Component({
  templateUrl: './notification.component.html',
  selector: 'app-notification',
  standalone: true,
  imports: [MatButtonModule, NgClass, DatePipe, TranslatePipe, MatMenuModule, MatIconModule],
  styleUrl: './notification.component.scss',
})
export class NotificationComponent extends BaseComponent implements OnInit {
  notifications: Notification[] = [];

  NotificationStatus = NotificationStatus;

  private readonly notificationService = inject(NotificationService);
  private readonly authService = inject(AuthService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly chartImportService = inject(ChartImportService);
  private readonly router = inject(Router);

  ngOnInit(): void {
    this.sub$ = this.authService.notifications$.subscribe((notifications) => {
      this.notifications = notifications;
    });
  }

  get numOfUnread() {
    return this.notifications.filter((notification) => notification.status === NotificationStatus.Unread).length;
  }

  markAsRead(index?: number) {
    let notificationId: string | undefined = undefined;

    if (index !== undefined) {
      const notification = this.notifications[index];

      if (!notification || notification.status === NotificationStatus.Read) return;

      notification.status = NotificationStatus.Read;
      notificationId = notification._id;
    } else {
      if (this.numOfUnread === 0) return;

      this.notifications = this.notifications.map((notification) => ({
        ...notification,
        status: NotificationStatus.Read,
      }));
    }

    this.notificationService.updateNotification(NotificationStatus.Read, notificationId).subscribe({
      error: (error) => {
        captureException(error);
        this.snackbar.open(
          this.translate.instant(_('error.error-occured')),
          this.translate.instant(_('common.close')),
          {
            panelClass: 'snackbar-error',
          },
        );
      },
    });
  }

  /**
   * Chart-import notifications hand off to the job's chart page (its diff or failure) rather than leaving
   * the merchant in this generic list — see the module-level comment for how "chart-import
   * notification" is detected and its one backend-rooted limitation.
   */
  onNotificationClick(notification: Notification, index: number): void {
    this.markAsRead(index);

    if (notification.title === CHART_IMPORT_READY_TITLE || notification.title === CHART_IMPORT_FAILED_TITLE)
      this.navigateToChartImport();
  }

  private navigateToChartImport(): void {
    // Read the in-memory signal rather than calling `refreshActive()` — the backend's `/active`
    // endpoint only ever returns `queued`/`running`/`awaiting_review` jobs, so refetching here would
    // overwrite (and lose) a `failed` job that is only still known about because nothing cleared it
    // client-side. The service already keeps this signal current via its own SSE stream and its
    // app-init fetch, so there is nothing this component needs to refresh.
    const link = chartImportJobLink(this.chartImportService.activeJob());

    // No job in memory (e.g. after a reload of a finished one) or a legacy create-mode job with no
    // chart: marking the notification read is all there is to do.
    if (link) this.router.navigate(link);
  }
}
